import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import {
  allTrustsOf,
  getMeasurementDistribution,
  getMeasurementTimeSeries,
  getSurvivalSeries,
  type PathwayMatch,
  type TimeBucket,
} from "@thalamos/pathway-logic";

import {
  findBestFit,
  fitExponential,
  fitPolynomial,
  fitPowerLaw,
  generateCurvePoints,
  type CurveType,
  type FitResult,
} from "../stats/curveFitting.js";
import { TrendChart, type ChartPoint } from "./TrendChart.js";

type DistributionStats = ReturnType<typeof getMeasurementDistribution>;

// Individual patient's measurement for highlighting
export type PatientMeasurement = {
  patientId: string;
  measurementName: string;
  values: number[]; // Can have multiple values if multiple pathway matches
};

// Props for the expanded distribution modal
type MeasurementDistributionModalProps = {
  name: string;
  matches: PathwayMatch[];
  numPatients: number;
  measurementType?: "duration" | "count";
  isOpen: boolean;
  onClose: () => void;
  highlightedPatient?: PatientMeasurement; // Optional patient to highlight
  initialCurveType?: CurveType; // Optional initial curve type from URL
  onCurveTypeChange?: (curveType: CurveType) => void; // Callback when curve type changes
};

// Create histogram bins with configurable count
function createBins(
  dist: DistributionStats,
  binCount: number,
): { min: number; max: number; count: number }[] {
  if (dist.values.length === 0) return [];

  const range = dist.max - dist.min;
  const binWidth = range === 0 ? 1 : Math.max(0.5, range / binCount);
  const actualBinCount = range === 0 ? 1 : Math.ceil(range / binWidth);

  const bins = Array.from({ length: actualBinCount }, (_, i) => {
    const binMin = dist.min + i * binWidth;
    return { min: binMin, max: binMin + binWidth, count: 0 };
  });

  // Single pass: drop each value into its bin rather than re-filtering all
  // values per bin (was O(bins × values)). Values on a boundary fall into the
  // higher bin, and the final bin is inclusive of dist.max — matching the old
  // half-open/closed-last behaviour.
  for (const v of dist.values) {
    const idx = Math.min(
      actualBinCount - 1,
      Math.max(0, Math.floor((v - dist.min) / binWidth)),
    );
    const bin = bins[idx];
    if (bin !== undefined) {
      bin.count++;
    }
  }

  return bins;
}

// Stat display component
const Stat = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) => (
  <div
    style={{
      padding: "8px 12px",
      background: highlight ? "#e0e7ff" : "#f3f4f6",
      borderRadius: 6,
      textAlign: "center",
    }}
  >
    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>
      {label}
    </div>
    <div
      style={{
        fontSize: 16,
        fontWeight: 700,
        color: highlight ? "#4f46e5" : "#111827",
      }}
    >
      {value}
    </div>
  </div>
);

export const MeasurementDistributionModal = ({
  name,
  matches,
  numPatients,
  measurementType = "duration",
  isOpen,
  onClose,
  highlightedPatient,
  initialCurveType,
  onCurveTypeChange,
}: MeasurementDistributionModalProps) => {
  const [curveType, setCurveType] = useState<CurveType>(
    initialCurveType ?? "none",
  );

  // Unit suffix and label based on measurement type
  const unit = measurementType === "count" ? "×" : "d";
  const unitLabel = measurementType === "count" ? "Count" : "Duration (days)";

  // --- Metric over time (trend) ---
  const [trustFilter, setTrustFilter] = useState<string | null>(null);
  const [bucket, setBucket] = useState<TimeBucket>("month");
  const [anchor, setAnchor] = useState<"start" | "end">("start");
  const [mode, setMode] = useState<"median" | "survival">("median");
  const [horizonDays, setHorizonDays] = useState(90);
  const [windowStart, setWindowStart] = useState<Date | null>(() => {
    const now = new Date();
    return new Date(
      Date.UTC(now.getUTCFullYear() - 1, now.getUTCMonth(), now.getUTCDate()),
    );
  });
  const [windowEnd, setWindowEnd] = useState<Date | null>(null);
  const [excludeExpired, setExcludeExpired] = useState(false);

  const trustOptions = useMemo(() => {
    const set = new Set<string>();
    matches.forEach((m) => {
      allTrustsOf(m).forEach((t) => set.add(t));
    });
    return Array.from(set).sort();
  }, [matches]);

  // Plausibility clamp: ignore junk timestamps (pre-2000 anchors, future dates)
  // so headline stats / medians aren't wrecked by bad data.
  const bounds = useMemo(
    () => ({
      minDate: new Date("2000-01-01T00:00:00.000Z"),
      maxDate: new Date(),
    }),
    [],
  );

  const trend = useMemo(
    () =>
      getMeasurementTimeSeries(matches, name, {
        bucket,
        anchor,
        trustFilter,
        windowStart: windowStart ?? undefined,
        windowEnd: windowEnd ?? undefined,
        bounds,
        excludeExpired,
      }),
    [
      matches,
      name,
      bucket,
      anchor,
      trustFilter,
      windowStart,
      windowEnd,
      bounds,
      excludeExpired,
    ],
  );

  const survival = useMemo(
    () =>
      getSurvivalSeries(matches, name, {
        horizonDays,
        bucket,
        anchor,
        trustFilter,
        windowStart: windowStart ?? undefined,
        windowEnd: windowEnd ?? undefined,
        bounds,
        excludeExpired,
      }),
    [
      matches,
      name,
      horizonDays,
      bucket,
      anchor,
      trustFilter,
      windowStart,
      windowEnd,
      bounds,
      excludeExpired,
    ],
  );

  const chartPoints: ChartPoint[] = useMemo(() => {
    if (mode === "survival") {
      return survival.map((p) => ({
        bucket: p.bucket,
        value: Math.round(p.rate * 100),
        n: p.n,
      }));
    }
    return trend.map((p) => ({
      bucket: p.bucket,
      value: p.value,
      n: p.n,
      lo: p.p25,
      hi: p.p75,
      provisional: p.n > 0 && p.complete / p.n < 0.8,
    }));
  }, [mode, survival, trend]);

  const dist = useMemo(
    () => getMeasurementDistribution(matches, name, bounds),
    [matches, name, bounds],
  );

  // Finer-grained bins for larger display
  const bins = useMemo(() => {
    if (dist.values.length === 0) return [];
    // More bins for larger modal - aim for ~40 bins or sqrt(n)*2, whichever is larger
    const targetBinCount = Math.max(
      40,
      Math.ceil(Math.sqrt(dist.values.length) * 2),
    );
    return createBins(dist, targetBinCount);
  }, [dist]);

  // Auto-select a best fit only while the user hasn't chosen a curve yet
  // (curveType still "none"). Guarding on this stops a filter change — which
  // recomputes `bins` — from overriding a manual selection.
  useEffect(() => {
    if (
      (initialCurveType === undefined || initialCurveType === "none") &&
      curveType === "none" &&
      bins.length >= 3
    ) {
      const bestFit = findBestFit(bins, 0.5);
      if (bestFit) {
        setCurveType(bestFit.type);
        onCurveTypeChange?.(bestFit.type);
      }
    }
  }, [bins, curveType, initialCurveType, onCurveTypeChange]);

  // Handle curve type change
  const handleCurveTypeChange = (newType: CurveType) => {
    setCurveType(newType);
    onCurveTypeChange?.(newType);
  };

  // Compute curve fit based on selected type
  const curveFit: FitResult | null = useMemo(() => {
    if (curveType === "none" || bins.length < 3) return null;
    switch (curveType) {
      case "polynomial":
        return fitPolynomial(bins, 2);
      case "power-law":
        return fitPowerLaw(bins);
      case "exponential":
        return fitExponential(bins);
      default:
        return null;
    }
  }, [curveType, bins]);

  const downloadCsv = () => {
    const csvContent = `${name} (${unitLabel})\n` + dist.values.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name.replace(/[^a-z0-9]/gi, "_")}_measurements.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const maxCount = Math.max(...bins.map((b) => b.count), 1);
  const chartHeight = 250;
  const chartWidth = 600;
  // Guard against empty bins so the widths don't become Infinity (division by 0).
  const binCount = Math.max(bins.length, 1);
  const barWidth = Math.max(2, (chartWidth / binCount) * 0.9);

  // Anchor-aware labels for the over-time chart. "start" = by the period's
  // start date (recent buckets censored → use % past horizon); "end" = by the
  // period's end date (of periods concluding then), excluding still-active ones.
  const aggWord = measurementType === "count" ? "sum" : "median";
  const anchorWord = anchor === "end" ? "end" : "start";
  const trendLabel =
    mode === "survival"
      ? anchor === "end"
        ? `${name} — % lasting ≥ ${String(horizonDays)}d, by end ${bucket}`
        : `${name} — % still in-state at ${String(horizonDays)}d, by start ${bucket}`
      : `${name} — ${
          measurementType === "count" ? "total" : "median"
        } by ${anchorWord} ${bucket}`;
  const trendHint =
    mode === "survival"
      ? anchor === "end"
        ? `% of periods ending in each ${bucket} that lasted ≥ ${String(horizonDays)}d · ongoing excluded · point colour = cohort size (darker = more)`
        : `% still in-state at ${String(horizonDays)}d, by start ${bucket} · immature cohorts excluded · point colour = cohort size (darker = more)`
      : anchor === "end"
        ? `${aggWord} of periods ending per ${bucket} · ongoing excluded · p25–p75 band · point colour = cohort size (darker = more)`
        : `${aggWord} per ${bucket} · p25–p75 band · dashed = provisional · point colour = cohort size (darker = more)`;
  const barGap = (chartWidth / binCount) * 0.1;

  // Calculate positions for highlighted patient values
  // For count measurements, center on the bin; for duration, use exact position
  const lastBin = bins.at(-1);
  const highlightPositions =
    highlightedPatient?.values.map((v) => {
      // Find the bin index that contains this value
      const binIndex = bins.findIndex((bin) => v >= bin.min && v < bin.max);
      // If not found in any bin, check if it equals the max of the last bin
      const actualBinIndex =
        binIndex >= 0
          ? binIndex
          : lastBin !== undefined && v === lastBin.max
            ? bins.length - 1
            : -1;

      // For count measurements (discrete), center on bin; for duration (continuous), use exact position
      const x =
        actualBinIndex >= 0
          ? actualBinIndex * (barWidth + barGap) + barWidth / 2
          : dist.max === dist.min
            ? chartWidth / 2
            : ((v - dist.min) / (dist.max - dist.min)) * chartWidth;

      return { value: v, x };
    }) ?? [];

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: 12,
          padding: 24,
          width: "min(90vw, 720px)",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 20, color: "#111827" }}>
              {name}
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
              Distribution of {dist.values.length} measurements from{" "}
              {numPatients} patients
            </p>
            {highlightedPatient && (
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 12,
                  color: "#dc2626",
                  fontWeight: 600,
                }}
              >
                This patient:{" "}
                {highlightedPatient.values
                  .map((v) => `${String(v)}${unit}`)
                  .join(", ")}
              </p>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {/* Curve fitting dropdown */}
            <select
              value={curveType}
              onChange={(e) => {
                handleCurveTypeChange(e.target.value as CurveType);
              }}
              style={{
                padding: "8px 12px",
                fontSize: 12,
                background: "#f3f4f6",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                cursor: "pointer",
                color: "#374151",
                fontWeight: 500,
              }}
              title="Fit a curve to the distribution"
            >
              <option value="none">No curve fit</option>
              <option value="polynomial">Polynomial (quadratic)</option>
              <option value="power-law">Power law decay</option>
              <option value="exponential">Exponential decay</option>
            </select>
            <button
              onClick={downloadCsv}
              style={{
                padding: "8px 14px",
                fontSize: 12,
                background: "#e5e7eb",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                color: "#374151",
                fontWeight: 500,
              }}
              title="Download measurement values as CSV"
            >
              ↓ Download CSV
            </button>
            <button
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                border: "none",
                background: "#f3f4f6",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 20,
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 8,
            marginBottom: 20,
          }}
        >
          <Stat label="Min" value={`${String(dist.min)}${unit}`} />
          <Stat label="P25" value={`${String(dist.p25)}${unit}`} />
          <Stat
            label="Median"
            value={`${String(dist.median)}${unit}`}
            highlight
          />
          <Stat label="Mean" value={`${dist.mean.toFixed(1)}${unit}`} />
          <Stat label="P75" value={`${String(dist.p75)}${unit}`} />
          <Stat label="Max" value={`${String(dist.max)}${unit}`} />
        </div>

        {/* Metric over time */}
        <div
          style={{
            background: "#f9fafb",
            padding: 16,
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            <strong style={{ fontSize: 13 }}>Over time</strong>
            <select
              value={mode}
              onChange={(e) => {
                setMode(e.target.value as "median" | "survival");
              }}
              style={{ fontSize: 12 }}
            >
              <option value="median">
                {measurementType === "count" ? "Total" : "Median"}
              </option>
              {measurementType !== "count" && (
                <option value="survival">% past horizon</option>
              )}
            </select>
            {mode === "survival" && (
              <label
                style={{
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                horizon
                <input
                  type="number"
                  min={1}
                  value={horizonDays}
                  onChange={(e) => {
                    setHorizonDays(Math.max(1, Number(e.target.value) || 1));
                  }}
                  style={{ width: 56, fontSize: 12 }}
                />
                d
              </label>
            )}
            <select
              value={bucket}
              onChange={(e) => {
                setBucket(e.target.value as TimeBucket);
              }}
              style={{ fontSize: 12 }}
            >
              <option value="month">Monthly</option>
              <option value="quarter">Quarterly</option>
              <option value="year">Yearly</option>
            </select>
            <select
              value={anchor}
              onChange={(e) => {
                setAnchor(e.target.value as "start" | "end");
              }}
              style={{ fontSize: 12 }}
            >
              <option value="start">by start date</option>
              <option value="end">by end date</option>
            </select>
            <select
              value={trustFilter ?? ""}
              onChange={(e) => {
                setTrustFilter(e.target.value || null);
              }}
              style={{ fontSize: 12 }}
            >
              <option value="">All trusts</option>
              {trustOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <label
              style={{
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              from
              <input
                type="date"
                value={
                  windowStart ? windowStart.toISOString().slice(0, 10) : ""
                }
                onChange={(e) => {
                  setWindowStart(
                    e.target.value
                      ? new Date(e.target.value + "T00:00:00.000Z")
                      : null,
                  );
                }}
                style={{ fontSize: 12 }}
              />
              to
              <input
                type="date"
                value={windowEnd ? windowEnd.toISOString().slice(0, 10) : ""}
                onChange={(e) => {
                  setWindowEnd(
                    e.target.value
                      ? new Date(e.target.value + "T23:59:59.999Z")
                      : null,
                  );
                }}
                style={{ fontSize: 12 }}
              />
            </label>
            <label
              style={{
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <input
                type="checkbox"
                checked={excludeExpired}
                onChange={(e) => {
                  setExcludeExpired(e.target.checked);
                }}
              />
              exclude auto-expired
            </label>
            <span style={{ fontSize: 11, color: "#6b7280" }}>{trendHint}</span>
          </div>
          <TrendChart
            points={chartPoints}
            width={chartWidth + 60}
            height={220}
            unit={mode === "survival" ? "%" : unit}
            label={trendLabel}
          />
        </div>

        {/* Large Histogram */}
        {bins.length > 0 && (
          <div
            style={{
              background: "#f9fafb",
              padding: 16,
              borderRadius: 8,
              border: "1px solid #e5e7eb",
            }}
          >
            <svg
              width={chartWidth + 60}
              height={chartHeight + 50}
              style={{ overflow: "visible" }}
            >
              {/* Y axis */}
              <g transform="translate(50, 0)">
                <line
                  x1={0}
                  y1={0}
                  x2={0}
                  y2={chartHeight}
                  stroke="#d1d5db"
                  strokeWidth={1}
                />
                {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                  const y = chartHeight - tick * chartHeight;
                  const value = Math.round(tick * maxCount);
                  return (
                    <g key={tick}>
                      <line
                        x1={-4}
                        y1={y}
                        x2={0}
                        y2={y}
                        stroke="#9ca3af"
                        strokeWidth={1}
                      />
                      <text
                        x={-8}
                        y={y}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fontSize={10}
                        fill="#6b7280"
                      >
                        {value}
                      </text>
                    </g>
                  );
                })}
                <text
                  x={-35}
                  y={chartHeight / 2}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#6b7280"
                  transform={`rotate(-90, -35, ${String(chartHeight / 2)})`}
                >
                  Count
                </text>
              </g>

              {/* Bars */}
              <g transform="translate(50, 0)">
                {bins.map((bin, i) => {
                  const barHeight =
                    maxCount > 0 ? (bin.count / maxCount) * chartHeight : 0;
                  const x = i * (barWidth + barGap);
                  const isP25 = bin.min <= dist.p25 && bin.max > dist.p25;
                  const isMedian =
                    bin.min <= dist.median && bin.max > dist.median;
                  const isP75 = bin.min <= dist.p75 && bin.max > dist.p75;

                  // Check if this bin contains a highlighted patient value
                  const hasHighlight = highlightPositions.some(
                    (hp) => bin.min <= hp.value && hp.value < bin.max,
                  );

                  return (
                    <g key={i}>
                      <rect
                        x={x}
                        y={chartHeight - barHeight}
                        width={barWidth}
                        height={barHeight}
                        fill={
                          hasHighlight
                            ? "#dc2626"
                            : isMedian
                              ? "#4f46e5"
                              : isP25 || isP75
                                ? "#818cf8"
                                : "#c7d2fe"
                        }
                        rx={1}
                        style={{ transition: "fill 0.2s" }}
                      />
                      <title>
                        {bin.min.toFixed(1)}-{bin.max.toFixed(1)}{" "}
                        {measurementType === "count" ? "occurrences" : "days"}:{" "}
                        {bin.count} pathways
                      </title>
                    </g>
                  );
                })}

                {/* Percentile markers - group by bin to avoid overlapping */}
                {(() => {
                  const percentiles = [
                    { value: dist.p25, label: "P25", color: "#818cf8" },
                    { value: dist.median, label: "Median", color: "#4f46e5" },
                    { value: dist.p75, label: "P75", color: "#818cf8" },
                  ];

                  // Group percentiles by their bin index
                  const grouped = new Map<
                    number,
                    { labels: string[]; color: string; x: number }
                  >();

                  percentiles.forEach(({ value, label, color }) => {
                    const binIndex = bins.findIndex(
                      (bin) => value >= bin.min && value < bin.max,
                    );
                    const actualBinIndex =
                      binIndex >= 0
                        ? binIndex
                        : lastBin !== undefined && value === lastBin.max
                          ? bins.length - 1
                          : -1;

                    const x =
                      actualBinIndex >= 0
                        ? actualBinIndex * (barWidth + barGap) + barWidth / 2
                        : dist.max === dist.min
                          ? chartWidth / 2
                          : ((value - dist.min) / (dist.max - dist.min)) *
                            chartWidth;

                    const key =
                      actualBinIndex >= 0 ? actualBinIndex : Math.round(x);

                    const existing = grouped.get(key);
                    if (existing) {
                      existing.labels.push(label);
                      // Use median color (darker) if median is in the group
                      if (label === "Median") existing.color = color;
                    } else {
                      grouped.set(key, { labels: [label], color, x });
                    }
                  });

                  return Array.from(grouped.values()).map(
                    ({ labels, color, x }) => (
                      <g key={labels.join("-")}>
                        <line
                          x1={x}
                          y1={0}
                          x2={x}
                          y2={chartHeight}
                          stroke={color}
                          strokeDasharray="4,2"
                          strokeWidth={1.5}
                        />
                        <text
                          x={x}
                          y={-6}
                          textAnchor="middle"
                          fontSize={9}
                          fill={color}
                          fontWeight={600}
                        >
                          {labels.join("/")}
                        </text>
                      </g>
                    ),
                  );
                })()}

                {/* Highlighted patient value markers */}
                {highlightPositions.map((hp, i) => (
                  <g key={`highlight-${String(i)}`}>
                    <line
                      x1={hp.x}
                      y1={0}
                      x2={hp.x}
                      y2={chartHeight}
                      stroke="#dc2626"
                      strokeWidth={2}
                    />
                    <circle
                      cx={hp.x}
                      cy={chartHeight + 15}
                      r={6}
                      fill="#dc2626"
                    />
                    <text
                      x={hp.x}
                      y={chartHeight + 30}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#dc2626"
                      fontWeight={600}
                    >
                      {hp.value}
                      {unit}
                    </text>
                  </g>
                ))}

                {/* Fitted curve overlay */}
                {curveFit &&
                  bins.length > 0 &&
                  (() => {
                    const curvePoints = generateCurvePoints(
                      curveFit,
                      dist.min,
                      dist.max,
                      100,
                    );
                    // Scale to chart coordinates
                    const pathData = curvePoints
                      .map((pt, i) => {
                        const x =
                          dist.max === dist.min
                            ? chartWidth / 2
                            : ((pt.x - dist.min) / (dist.max - dist.min)) *
                              chartWidth;
                        const y = chartHeight - (pt.y / maxCount) * chartHeight;
                        // Clamp y to chart bounds
                        const clampedY = Math.max(0, Math.min(chartHeight, y));
                        return `${i === 0 ? "M" : "L"} ${String(x)} ${String(clampedY)}`;
                      })
                      .join(" ");
                    return (
                      <path
                        d={pathData}
                        fill="none"
                        stroke="#059669"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity={0.9}
                      />
                    );
                  })()}
              </g>

              {/* X axis */}
              <g transform={`translate(50, ${String(chartHeight)})`}>
                <line
                  x1={0}
                  y1={0}
                  x2={chartWidth}
                  y2={0}
                  stroke="#d1d5db"
                  strokeWidth={1}
                />
                {/* Show appropriate number of tick labels based on data range */}
                {(() => {
                  const range = dist.max - dist.min;
                  // For small discrete ranges, show each integer value
                  if (
                    range <= 10 &&
                    Number.isInteger(dist.min) &&
                    Number.isInteger(dist.max)
                  ) {
                    const ticks: number[] = [];
                    for (let v = dist.min; v <= dist.max; v++) {
                      ticks.push(v);
                    }
                    return ticks.map((value) => {
                      const x =
                        range === 0
                          ? chartWidth / 2
                          : ((value - dist.min) / range) * chartWidth;
                      return (
                        <g key={value}>
                          <line
                            x1={x}
                            y1={0}
                            x2={x}
                            y2={4}
                            stroke="#9ca3af"
                            strokeWidth={1}
                          />
                          <text
                            x={x}
                            y={16}
                            textAnchor="middle"
                            fontSize={10}
                            fill="#6b7280"
                          >
                            {value}
                            {unit}
                          </text>
                        </g>
                      );
                    });
                  }
                  // For larger ranges, use ~5 evenly spaced ticks
                  return [0, 0.25, 0.5, 0.75, 1].map((tick) => {
                    const x = tick * chartWidth;
                    const value = dist.min + tick * range;
                    return (
                      <g key={tick}>
                        <line
                          x1={x}
                          y1={0}
                          x2={x}
                          y2={4}
                          stroke="#9ca3af"
                          strokeWidth={1}
                        />
                        <text
                          x={x}
                          y={16}
                          textAnchor="middle"
                          fontSize={10}
                          fill="#6b7280"
                        >
                          {value.toFixed(0)}
                          {unit}
                        </text>
                      </g>
                    );
                  });
                })()}
                <text
                  x={chartWidth / 2}
                  y={35}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#6b7280"
                >
                  {unitLabel}
                </text>
              </g>
            </svg>
          </div>
        )}

        {/* Curve Fit Results */}
        {curveFit && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: "#ecfdf5",
              borderRadius: 6,
              border: "1px solid #a7f3d0",
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div
              style={{
                width: 16,
                height: 4,
                background: "#059669",
                borderRadius: 2,
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#065f46" }}>
                {curveType === "polynomial"
                  ? "Quadratic Fit"
                  : curveType === "power-law"
                    ? "Power Law Fit"
                    : "Exponential Decay Fit"}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#047857",
                  fontFamily: "monospace",
                  marginTop: 4,
                }}
              >
                {curveFit.formula}
              </div>
            </div>
            <div
              style={{
                textAlign: "right",
                padding: "6px 12px",
                background: "#d1fae5",
                borderRadius: 4,
              }}
            >
              <div style={{ fontSize: 10, color: "#065f46" }}>
                Goodness of Fit
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color:
                    curveFit.rSquared >= 0.8
                      ? "#059669"
                      : curveFit.rSquared >= 0.5
                        ? "#d97706"
                        : "#dc2626",
                }}
              >
                R² = {curveFit.rSquared.toFixed(3)}
              </div>
            </div>
          </div>
        )}

        {/* Legend for highlighted patient */}
        {highlightedPatient && highlightPositions.length > 0 && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "#fef2f2",
              borderRadius: 6,
              border: "1px solid #fecaca",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                background: "#dc2626",
                borderRadius: "50%",
              }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#991b1b" }}>
                This Patient
              </div>
              <div style={{ fontSize: 12, color: "#7f1d1d" }}>
                {highlightPositions.length === 1 &&
                highlightPositions[0] !== undefined
                  ? `Measurement: ${String(highlightPositions[0].value)}${unit}`
                  : `${String(highlightPositions.length)} pathway matches: ${highlightPositions.map((hp) => `${String(hp.value)}${unit}`).join(", ")}`}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default MeasurementDistributionModal;
